import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { ConfirmLeaveModal } from '@components/ConfirmLeaveModal/ConfirmLeaveModal'
import { TownNotificationsButton } from '@components/NotificationSettings/NotificationsSettingsButton'
import { Panel } from '@components/Panel/Panel'
import { PanelButton } from '@components/Panel/PanelButton'
import { SetUsernameDisplayName } from '@components/SetUsernameDisplayName/SetUsernameDisplayName'
import { useUserList } from '@components/UserList/UserList'
import { isDefined } from '@towns-protocol/sdk'
import { Box, Icon, Stack, Text, TextButton, TextField } from '@ui'
import { useMyAbstractAccountAddress } from 'hooks/useAbstractAccountAddress'
import { Analytics } from 'hooks/useAnalytics'
import { useCreateLink } from 'hooks/useCreateLink'
import { useDevice } from 'hooks/useDevice'
import { CHANNEL_INFO_PARAMS } from 'routes'
import {
    DmAndGdmModel,
    DMChannelContextUserLookupProvider,
    useChannelData,
    useDMData,
    useDmsAndGdms,
    useMemberIds,
    useMyUserId,
    useTownsClient,
    useTownsContext,
    useUserLookupContext,
} from 'use-towns-client'
import { getPrettyDisplayName } from 'utils/getPrettyDisplayName'

import { ChannelMembersModal } from '../components/ChannelMembersPanel/ChannelMembersPanel'

import { usePanelActions } from './layouts/hooks/usePanelActions'
import { GDMChannelPermissionsModal } from './GDMChannelPermissions'

export const DMChannelInfoPanel = () => {
    const channelId = useChannelData()?.channel?.id
    const { data } = useDMData(channelId)
    const title = data ? (data.isGdm ? 'Group Info' : 'Direct Message') : ''
    return <Panel label={title}>{<DMChannelInfo channelId={channelId} data={data} />}</Panel>
}

export const DMChannelInfo = (props: { channelId?: string; data?: DmAndGdmModel }) => {
    const { channelId, data } = props
    const { data: abstractAccountAddress } = useMyAbstractAccountAddress()
    const [activeModal, setActiveModal] = useState<
        'members' | 'permissions' | 'confirm-leave' | undefined
    >(undefined)
    const { leaveRoom } = useTownsClient()
    const { isTouch } = useDevice()

    const { memberIds } = useMemberIds(channelId)
    const { lookupUser } = useUserLookupContext()
    const members = useMemo(
        () => memberIds.map((userId) => lookupUser(userId)),
        [lookupUser, memberIds],
    )

    const myUserId = useMyUserId()

    const navigate = useNavigate()

    const { createLink } = useCreateLink()
    const { spaces } = useTownsContext()
    const { dmsAndGdms } = useDmsAndGdms()

    const onLeaveClick = useCallback(() => {
        const tracked = {
            channelId,
            numberOfMembers: memberIds.length,
        }
        Analytics.getInstance().track('clicked leave group', tracked, () => {
            console.log('[analytics] clicked leave group', tracked)
        })
        setActiveModal('confirm-leave')
    }, [channelId, memberIds.length])

    const onLeaveConfirm = useCallback(async () => {
        if (!channelId) {
            return
        }
        const tracked = {
            channelId,
            numberOfRemainingMembers: memberIds.length - 1,
        }
        Analytics.getInstance().track('confirmed leave group', tracked, () => {
            console.log('[analytics] confirmed leave group', tracked)
        })

        await leaveRoom(channelId)
        setActiveModal(undefined)

        const availableSpaces = spaces.filter((space) => space.id !== channelId)
        const availableDmChannels = dmsAndGdms.filter(
            (channel) => !channel.left && channel.id !== channelId,
        )

        if (availableDmChannels.length > 0) {
            const firstChat = availableDmChannels[0]
            const chatLink = createLink({ messageId: firstChat.id })
            if (chatLink) {
                navigate(chatLink)
                return
            }
        }

        if (availableSpaces.length > 0) {
            const spaceLink = createLink({
                spaceId: availableSpaces[0].id,
                panel: 'townInfo' as const,
            })
            if (spaceLink) {
                navigate(spaceLink)
                return
            }
        }

        const messagesLink = createLink({ route: 'messages' as const })
        console.log('[DMChannelInfoPanel] No chats remaining, navigating to empty state:', {
            messagesLink,
            fallbackRoute: '/messages',
        })
        if (messagesLink) {
            navigate(messagesLink)
        } else {
            navigate('/messages')
        }
    }, [channelId, createLink, leaveRoom, memberIds.length, navigate, spaces, dmsAndGdms])

    const onLeaveCancel = useCallback(() => {
        const tracked = {
            channelId,
        }
        Analytics.getInstance().track('canceled leave group', tracked, () => {
            console.log('[analytics] canceled leave group', tracked)
        })
        setActiveModal(undefined)
    }, [channelId])

    const { openPanel } = usePanelActions()
    const onMembersClick = useCallback(() => {
        if (isTouch) {
            setActiveModal('members')
        } else {
            openPanel(CHANNEL_INFO_PARAMS.DIRECTORY)
        }
    }, [isTouch, openPanel])

    // const onPermissionsClick = useCallback(() => {
    //     if (isTouch) {
    //         setActiveModal('permissions')
    //     } else {
    //         navigate(`../${CHANNEL_INFO_PARAMS.INFO}?${CHANNEL_INFO_PARAMS.PERMISSIONS}`)
    //     }
    // }, [navigate, isTouch])

    const membersExcludingSelf = useMemo(() => {
        return members.filter(isDefined).filter((member) => member.userId !== myUserId)
    }, [members, myUserId])

    const memberNamesExludingSelf = useMemo(() => {
        return membersExcludingSelf.map((m) => m?.userId).filter(isDefined)
    }, [membersExcludingSelf])

    // eslint-disable-next-line @typescript-eslint/no-base-to-string
    const membersText = useUserList({ userIds: memberNamesExludingSelf, excludeSelf: true }).join(
        '',
    )
    const memberCount = members.length ?? 0

    const isGDM = data?.isGdm ?? false

    return (
        <>
            <DMChannelContextUserLookupProvider
                channelId={channelId ?? ''}
                abstractAccountAddress={abstractAccountAddress}
            >
                <Stack gap>
                    {channelId && <SetUsernameDisplayName streamId={channelId} />}
                    {isGDM ? (
                        <>
                            {memberNamesExludingSelf.length > 0 && (
                                <Stack gap padding background="level2" rounded="sm">
                                    {isGDM ? (
                                        <RoomPropertiesInputField
                                            defaultTitle={membersText}
                                            data={data}
                                        />
                                    ) : (
                                        <Text fontWeight="medium" color="default">
                                            {membersText}
                                        </Text>
                                    )}
                                </Stack>
                            )}
                        </>
                    ) : (
                        <>
                            {membersExcludingSelf.map((member) => (
                                <ProfilePanelButton key={member?.userId} member={member} />
                            ))}
                        </>
                    )}
                    {isGDM && (
                        <PanelButton onClick={onMembersClick}>
                            <Icon type="people" size="square_sm" color="gray2" />
                            <Text color="default" fontWeight="medium">
                                {memberCount} {memberCount === 1 ? 'member' : 'members'}{' '}
                            </Text>
                        </PanelButton>
                    )}

                    {channelId && (
                        <TownNotificationsButton
                            type={isGDM ? 'gdm' : 'dm'}
                            channelId={channelId}
                        />
                    )}

                    <PanelButton onClick={onLeaveClick}>
                        <Icon type="logout" color="error" size="square_sm" />
                        <Text color="error" fontWeight="medium">
                            Leave group
                        </Text>
                    </PanelButton>
                </Stack>
                {activeModal === 'confirm-leave' && (
                    <ConfirmLeaveModal
                        text="Leave group?"
                        onConfirm={onLeaveConfirm}
                        onCancel={onLeaveCancel}
                    />
                )}
                {activeModal === 'members' && (
                    <ChannelMembersModal onHide={() => setActiveModal(undefined)} />
                )}
                {activeModal === 'permissions' && (
                    <GDMChannelPermissionsModal onHide={() => setActiveModal(undefined)} />
                )}
            </DMChannelContextUserLookupProvider>
        </>
    )
}

const ProfilePanelButton = (props: {
    member: {
        userId: string
        displayName: string
        username?: string
    }
}) => {
    const { member } = props
    const { createLink } = useCreateLink()

    const profileLink = createLink({ profileId: member.userId })
    const navigate = useNavigate()
    const onClick = useCallback(() => {
        if (profileLink) {
            navigate(profileLink)
        }
    }, [profileLink, navigate])

    return (
        <PanelButton onClick={onClick}>
            <Text fontWeight="medium" color="default">
                {getPrettyDisplayName(member)}
            </Text>
        </PanelButton>
    )
}

const RoomPropertiesInputField = (props: { defaultTitle: string; data?: DmAndGdmModel }) => {
    const { data, defaultTitle } = props
    const { setRoomProperties } = useTownsClient()
    const [isEditing, setIsEditing] = useState(false)
    const [value, setValue] = useState('hello world')

    const validationError = value.length < 32 ? undefined : 'Title must be less than 32 characters'

    const onChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        setValue(e.target.value)
    }, [])

    const onSave = useCallback(async () => {
        if (!data?.id) {
            return
        }

        await setRoomProperties(data?.id, value, data.properties?.topic ?? '')
        setIsEditing(false)
    }, [setRoomProperties, data?.id, data?.properties, value])

    const onKeyDown = useCallback(
        (e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === 'Enter') {
                onSave()
            } else if (e.key === 'Escape') {
                setValue(data?.properties?.name ?? '')
                setIsEditing(false)
            }
        },
        [onSave, data, setValue, setIsEditing],
    )
    const onBlur = useCallback(() => {
        if (value !== data?.properties?.name) {
            return
        }
        setIsEditing(false)
    }, [data?.properties, setIsEditing, value])

    useEffect(() => {
        setValue(data?.properties?.name ?? '')
    }, [data?.properties?.name])

    return (
        <Stack gap>
            <Text color="gray2" fontSize="sm">
                Group name
            </Text>
            {isEditing ? (
                <>
                    <Stack horizontal alignItems="center" gap="sm">
                        <TextField
                            border
                            autoFocus
                            tone={validationError ? 'error' : 'neutral'}
                            background="level3"
                            width="100%"
                            value={value}
                            height="height_lg"
                            paddingX="sm"
                            placeholder="Enter group name"
                            autoCorrect="off"
                            onChange={onChange}
                            onBlur={onBlur}
                            onKeyDown={onKeyDown}
                        />
                        <Box grow />
                        <TextButton disabled={validationError !== undefined} onClick={onSave}>
                            Save
                        </TextButton>
                    </Stack>
                    {validationError && (
                        <Text color="error" fontSize="sm">
                            {validationError}
                        </Text>
                    )}
                </>
            ) : (
                <Stack horizontal alignItems="center" gap="sm">
                    <Text fontWeight="medium" color="default">
                        {value.length > 0 ? value : defaultTitle}
                    </Text>
                    <Box grow />
                    <TextButton onClick={() => setIsEditing(true)}>Edit</TextButton>
                </Stack>
            )}
        </Stack>
    )
}
